import React, { useState, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { isMatch } from 'lodash'
import OutsideClickHandler from 'react-outside-click-handler'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Scrollbars } from 'react-custom-scrollbars'
import { FormattedMessage } from 'react-intl'
import messages from 'utils/messages'
import { ChartSelect } from "components/../images/common"

const SelectMetricDataMenu = ({
  options = [],
  validTableName = '',
  onOptionSelected = (val) => null,
  onClose = (val) => null
}) => {

  const metricOptions = useMemo(() => {
    if (options && !options.length) return []

    return options
    .filter(data => data.tableName !== 'net')
    .map(data => ({
      tableName: data.tableName,
      metricOptions: data.metrics.map(metric => ({
        label: metric.name,
        value: metric.name,
        tableName: data.tableName,
        isDisabled: !!(validTableName && validTableName !== data.tableName)
      }))
    }))
  }, [options, validTableName])

  const handleOnOptionSelected = (optionSelected) => {
    if(!optionSelected.isDisabled && (!validTableName || validTableName === optionSelected.tableName)) {
      onOptionSelected(optionSelected)
    }
  }

  return (
    <OutsideClickHandler onOutsideClick={onClose}>
      <div 
        className='chart-editor-multiselect-metric-data-menu'
        onClick={event => event.stopPropagation()}
      > 
        {/* TODO added searcher input */}
        <Scrollbars
          autoHeight
          // autoHide
          // autoHideDuration={1}
          className="custom-scrollbars"
          autoHeightMax={214}
          universal
          renderThumbVertical={props => <div {...props} className="thumb-vertical" />}
        >
          { metricOptions.length === 0 ? 
            (
              <div className='chart-editor-multiselect-metric-data-menu__option'>
                <FormattedMessage {...messages.noDataAvailable} />
              </div>
            )
            :
            metricOptions.map((table, index) => {
              return (
                <React.Fragment key={table.tableName}>
  
                  <div className='chart-editor-multiselect-metric-data-menu__header'>
                    {`TableName ${table.tableName}`.toUpperCase()}
                  </div>
  
                  {table.metricOptions.map(metricOption => {
                    return (
                      <div
                        key={metricOption.value}
                        className={classNames('chart-editor-multiselect-metric-data-menu__option', {
                          'chart-editor-multiselect-metric-data-menu__option__disabled': metricOption.isDisabled
                        })}
                        onClick={() => handleOnOptionSelected(metricOption)}
                      >
                        <div className='chart-editor-multiselect-metric-data-menu__option__icon'>
                          <ChartSelect />
                        </div>
                        <div>
                          {metricOption.label}
                        </div>
                      </div>
                    )
                  })}
  
                  { (metricOptions.length-1 > index) &&
                    <div className='chart-editor-multiselect-metric-data-menu__separator'/>
                  }
                </React.Fragment>
              )
            })
          }
        </Scrollbars>
      </div>
    </OutsideClickHandler>
  )
}

const SelectSimpleMenu = ({
  options = [],
  withDashboardDefaultOption = false,
  onOptionSelected = (val) => null,
  onClose = (val) => null
}) => {

  const simpleOptions = useMemo(() => {
    if (options && !options.length) return []

    const opts = options.map(option => ({
      label: option,
      value: option
    }))

    if (withDashboardDefaultOption) {
      opts.unshift({label: 'Dashboard default', value: 'dashboard default'})
    }

    return opts
  }, [options])

  const handleOnOptionSelected = (optionSelected) => {
    onOptionSelected(optionSelected)
  }

  return (
    <OutsideClickHandler onOutsideClick={onClose}>
      <div 
        className='chart-editor-multiselect-simple-menu'
        onClick={event => event.stopPropagation()}
      >
        <Scrollbars
          autoHeight
          // autoHide
          // autoHideDuration={1}
          className="custom-scrollbars"
          autoHeightMax={214}
          universal
          renderThumbVertical={props => <div {...props} className="thumb-vertical" />}
        >
          { (simpleOptions && simpleOptions.length > 0) 
            ? 
              (simpleOptions.map((option, index) => (
                <div
                  className='chart-editor-multiselect-simple-menu__option'
                  key={index}
                  onClick={() => handleOnOptionSelected(option)}
                >
                  {option.label}
                </div>
              )))
            : 
              (
                <div className='chart-editor-multiselect-simple-menu__option'>
                  <FormattedMessage {...messages.noDataAvailable} />
                </div>
              )
          }
        </Scrollbars>
      </div>
    </OutsideClickHandler>
  )
}

export const MultiSelect = ({
  label = '', 
  placeholder = '',
  selectedOptions = [],
  options = [],
  validTableName = '',
  isSearchable = false,
  onChange = (val) => null,
  withSimpleMenu = false,
  withMetricDataMenu = false,
  showSelectedOptions = true,
  withDashboardDefaultOption = false 
}) => {

  const [currentSelectedOptions, setCurrentSelectedOptions] = useState(selectedOptions)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    if (!areArraysEqualWithSubsetCheck(selectedOptions, currentSelectedOptions)) {
      setCurrentSelectedOptions(selectedOptions)
    }
  }, [selectedOptions])

  const handleOnSelectedOption = (selectedOption) => {
    if (selectedOption.value === 'dashboard default') {
      onChange([])
      return
    }

    const optionIndex = currentSelectedOptions.findIndex(option => option.value === selectedOption.value)
    
    if (optionIndex === -1) {
      const newSelectedOptions = [...currentSelectedOptions, selectedOption]
      onChange(newSelectedOptions)
    } else {
      const newSelectedOptions = currentSelectedOptions.filter((_, index) => index !== optionIndex)
      onChange(newSelectedOptions)
    }
  }

  const handleOnMetricDataSelectedOption = (selectedMetric) => {
    const metricIndex = currentSelectedOptions
    .findIndex(metric => 
      metric.value === selectedMetric.value && metric.tableName === selectedMetric.tableName
    )
    if (metricIndex === -1) {
      const newSelectedOptions = [...currentSelectedOptions, selectedMetric]
      onChange(newSelectedOptions)
      // setCurrentSelectedOptions(() => {
      //   return newSelectedOptions
      // })
    } else {
      const newSelectedOptions = currentSelectedOptions.filter((_, index) => index !== metricIndex)
      onChange(newSelectedOptions)
      // setCurrentSelectedOptions(() => {
      //   return newSelectedOptions
      // })
    }
  }

  const removeSelectedOption = (optionIndex) => {
    const newSelectedOptions = currentSelectedOptions.filter((_, index) => index !== optionIndex)
    onChange(newSelectedOptions)
  }
  
  const handleToggleMenu = () => {
    setIsMenuOpen(prevState => !prevState)
  }

  return (
    <div className='chart-editor-multiselect'>
      {label &&
        <div className='chart-editor__label'>
          <label className="text-field__label1">{label}</label>
        </div>
      }
      <div 
        className='chart-editor-multiselect-input' 
        onClick={handleToggleMenu}
      >
        <div className='chart-editor-multiselect-input__box'>
          {currentSelectedOptions.length > 0 
            ? 
              showSelectedOptions 
              ? currentSelectedOptions.map((option, index) => (
                  <div 
                    key={index} 
                    className="chart-editor-multiselect-input__box__selected-option"
                    onClick={event => {
                      event.stopPropagation()
                      removeSelectedOption(index)
                    }}
                  >
                    {option.label}
                  </div>
                ))
              : <div> {`${currentSelectedOptions.length} selected`} </div>
            : placeholder 
              ? <div className="chart-editor-multiselect-input__box__placeholder" >
                  {placeholder}
                </div>
              : null
          }
        </div>
        <div className='chart-editor-multiselect-input__icon'>
          { isMenuOpen ? <ChevronUp /> : <ChevronDown /> }
        </div>

        { (withSimpleMenu && isMenuOpen) &&
          <SelectSimpleMenu
            options={options}
            withDashboardDefaultOption={withDashboardDefaultOption}
            onOptionSelected={val => {
              handleOnSelectedOption(val)
              handleToggleMenu()
            }}
            onClose={handleToggleMenu}
          />
        }

        { (withMetricDataMenu && isMenuOpen) &&
          <SelectMetricDataMenu
            options={options}
            validTableName={validTableName}
            onOptionSelected={val => {
              handleOnMetricDataSelectedOption(val)
              handleToggleMenu()
            }}
            onClose={handleToggleMenu}
          />
        }
      </div>
    </div>
  )
}

function areArraysEqualWithSubsetCheck(arr1, arr2) {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false
  if (arr1.length !== arr2.length) return false

  const arr2Copy = [...arr2]

  return arr1.every(obj1 => {
    const matchIndex = arr2Copy.findIndex(obj2 => isMatch(obj1, obj2) || isMatch(obj2, obj1))
    if (matchIndex === -1) {
      return false
    }
    arr2Copy.splice(matchIndex, 1)
    return true
  })
}